/**
 * ⭐ Favorite creatures (v3.09, per Javi) — the build-lab bookmark.
 * Starred in the Collection dossier, surfaced as a draft filter, so
 * re-running the same experiment stops meaning re-scrolling for it.
 *
 * Meta progress like stardust and the codex: one localStorage key,
 * loaded once, independent of run saves.
 *
 * ☁️ Cloud-synced since v3.61 (per Javi) — but NOT by union, unlike the
 * codex. A favorite can be taken away, and a grow-only merge would make
 * unstarring impossible: drop a star on the phone and the PC's older list
 * would put it straight back. So the whole list travels with the moment
 * it last changed, and the newer edit wins outright. Losing one starred
 * creature to a race is a shrug; being unable to unstar is a bug you feel
 * every day.
 */
const STORAGE_KEY = 'elemental-dispatch-favorites'
const STAMP_KEY = 'elemental-dispatch-favorites-at'

export function useFavorites() {
  const favorites = useState<string[]>('favorite-creatures', () => [])
  const loaded = useState('favorite-creatures-loaded', () => false)
  /** When this device last changed the list (ms). Decides cloud merges. */
  const changedAt = useState('favorite-creatures-at', () => 0)

  function load() {
    if (loaded.value || typeof localStorage === 'undefined') return
    loaded.value = true
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]')
      if (Array.isArray(stored)) favorites.value = stored.filter(v => typeof v === 'string')
      changedAt.value = Number(localStorage.getItem(STAMP_KEY) ?? 0) || 0
    } catch { /* corrupted storage — start clean */ }
  }

  function persist(stamp = true) {
    if (typeof localStorage === 'undefined') return
    if (stamp) changedAt.value = Date.now()
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites.value))
    localStorage.setItem(STAMP_KEY, String(changedAt.value))
  }

  /**
   * ☁️ Take the cloud's list when its edit is newer. The rule lives in
   * utils/metaMerge (mergeFavorites); adopting keeps the REMOTE stamp
   * rather than stamping now, so both devices agree on when the list
   * last actually changed.
   */
  function adopt(remote: string[], remoteAt: number) {
    load()
    const next = mergeFavorites({ list: favorites.value, at: changedAt.value }, { list: remote, at: remoteAt })
    if (!next) return
    favorites.value = next.list
    changedAt.value = next.at
    persist(false)
  }

  function has(defId: string): boolean {
    load()
    return favorites.value.includes(defId)
  }

  function toggle(defId: string): boolean {
    load()
    if (favorites.value.includes(defId)) {
      favorites.value = favorites.value.filter(id => id !== defId)
      persist()
      return false
    }
    favorites.value = [...favorites.value, defId]
    persist()
    return true
  }

  return { favorites, changedAt, load, has, toggle, adopt }
}
